import React, { useRef } from 'react';
import { Element } from 'react-scroll';
import './Skills.css';
import { motion, useInView, useReducedMotion } from 'framer-motion';
import { fadeInUp, staggerContainer, cardVariant } from '../../components/functions/animation';
import { skillCategories } from '../../data/skills';
import { SiReact, SiNodedotjs, SiPython, SiMongodb } from 'react-icons/si';
import { FaBrain, FaDatabase, FaServer, FaCode, FaLayerGroup } from 'react-icons/fa';

const CAT_ICONS = {
  'Full-Stack Web Development': <SiReact />,
  'Artificial Intelligence & Machine Learning': <FaBrain />,
  'Databases & Backend': <FaDatabase />,
  'DevOps & Deployment': <FaServer />,
  'Programming Languages': <FaCode />,
  'Computer Science Fundamentals': <FaLayerGroup />,
};

const LEVEL_PCT = { Expert: 90, Intermediate: 65, Learning: 35 };

const SkillBar = ({ name, level }) => {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, amount: 0.5 });
  const pct = LEVEL_PCT[level] ?? 40;
  return (
    <div className="skill-row" ref={ref}>
      <div className="skill-meta">
        <span className="skill-name">{name}</span>
        <span className={`skill-pct level-${level}`}>{pct}%</span>
      </div>
      <div className="skill-track">
        <motion.div
          className={`skill-fill level-${level}`}
          style={{ transformOrigin: 'left' }}
          initial={{ scaleX: 0 }}
          animate={inView ? { scaleX: pct / 100 } : { scaleX: 0 }}
          transition={{ duration: 1.2, ease: [0.22, 1, 0.36, 1], delay: 0.1 }}
        />
      </div>
    </div>
  );
};

const Skills = () => {
  const reduceMotion = useReducedMotion();
  return (
  <Element name="skills">
    <section id="skills">
      <div className="container">
        <motion.div variants={fadeInUp} initial="hidden" whileInView="show" viewport={{ once: true, amount: 0.2 }}>
          <p className="section-label">What I Know</p>
          <h2 className="heading">My <span>Skills</span></h2>
          <p className="section-quote-text">"The expert in anything was once a beginner who refused to quit."</p>
        </motion.div>

        <motion.div
          className="skills-grid"
          variants={staggerContainer}
          initial="hidden"
          whileInView="show"
          viewport={{ once: true, amount: 0.1 }}
        >
          {skillCategories.map((cat, idx) => (
            <motion.div
              className="skill-card"
              key={idx}
              custom={idx}
              variants={cardVariant}
              whileHover={reduceMotion ? undefined : { y: -6, scale: 1.02 }}
              transition={{ type: 'spring', stiffness: 260, damping: 22 }}
            >
              <div className="skill-card-header">
                <span className="skill-card-icon" aria-hidden="true">{CAT_ICONS[cat.category] || <FaCode />}</span>
                <h3 className="skill-card-head">{cat.category}</h3>
              </div>
              <div className="skill-bars">
                {cat.skills.map((skill, i) => (
                  <SkillBar key={i} name={skill.name} level={skill.level} />
                ))}
              </div>
            </motion.div>
          ))}
        </motion.div>

        <div className="skills-legend">
          <span className="legend-dot level-Expert" />Expert (90%)
          <span className="legend-dot level-Intermediate" />Intermediate (65%)
          <span className="legend-dot level-Learning" />Learning (35%)
        </div>
      </div>
    </section>
  </Element>
  );
};

export default Skills;
